import axios from 'axios';

// Create axios instance with authentication header
const apiClient = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'https://api.superchargeme.com'
});

// Add authorization header to all requests
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers['x-auth-token'] = token;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor for error handling
apiClient.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    // Default error message
    let errorMessage = 'An unexpected error occurred';
    let errorCode = 'UNKNOWN_ERROR';
    let statusCode = error.response?.status || 500;
    
    // Extract error details from response if available
    if (error.response?.data) {
      if (typeof error.response.data === 'string') {
        errorMessage = error.response.data;
      } else {
        errorMessage = error.response.data.message || errorMessage;
        errorCode = error.response.data.code || errorCode;
      }
    } else if (error.message) {
      errorMessage = error.message;
      
      // Special handling for network errors
      if (error.message === 'Network Error') {
        errorCode = 'NETWORK_ERROR';
        errorMessage = 'Unable to connect to the server. Please check your internet connection.';
      }
    }
    
    // Enhance the error object with custom properties
    const enhancedError = {
      ...error,
      userMessage: errorMessage,
      code: errorCode,
      statusCode
    };
    
    return Promise.reject(enhancedError);
  }
);

// Data service methods
const dataService = {
  // Get sales data
  getSalesData: async (brandId, timeRange, granularity) => {
    try {
      console.log(`Fetching sales data for brand ${brandId} with timeRange=${timeRange}, granularity=${granularity}`);
      const response = await apiClient.get('/api/analytics/revenue', {
        params: {
          brandId,
          timeRange
        }
      });
      
      // Transform response to expected format
      const data = response.data;
      return {
        data: data.chartData || [],
        totalOrders: data.totalOrders || 0,
        totalRevenue: Math.round(data.current || 0),  // Round to whole number
        growthRate: data.percentageChange || 0,
        orderGrowthRate: data.percentageChange || 0,
        hasRealData: data.sourceTable ? true : false,
        message: data.sourceTable ? `Data from ${data.sourceTable}` : 'Using mock data',
        isConsolidated: data.isConsolidated || false
      };
    } catch (error) {
      console.error('Error fetching sales data:', error);
      throw error;
    }
  },

  // Get marketing data
  getMarketingData: async (brandId, timeRange, granularity) => {
    try {
      console.log(`Fetching marketing data for brand ${brandId} with timeRange=${timeRange}`);
      const response = await apiClient.get('/api/analytics/marketing', {
        params: {
          brandId,
          timeRange
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching marketing data:', error);
      throw error;
    }
  },

  // Get product data
  getProductData: async (brandId, limit) => {
    try {
      console.log(`Fetching product data for brand ${brandId}`);
      const response = await apiClient.get('/api/analytics/products', {
        params: {
          brandId,
          timeRange: 'month'
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching product data:', error);
      throw error;
    }
  },

  // Get customer data
  getCustomerData: async (brandId, timeRange) => {
    try {
      console.log(`Fetching customer data for brand ${brandId} with timeRange=${timeRange}`);
      const response = await apiClient.get('/api/analytics/customers', {
        params: {
          brandId,
          timeRange: timeRange || 'week'  // Use dynamic timeRange, default to 'week'
        }
      });
      
      // Transform response to expected format
      const data = response.data;
      return {
        data: data.segments || [],
        growthRate: data.percentageChange || 0,
        totalCustomers: data.totalCustomers || 0,
        newCustomers: data.newCustomers || 0,
        returningCustomers: data.returningCustomers || 0
      };
    } catch (error) {
      console.error('Error fetching customer data:', error);
      throw error;
    }
  },

  // Get session data for conversion rate calculation
  getSessionData: async (brandId, timeRange) => {
    try {
      console.log(`Fetching session data for brand ${brandId}`);
      const response = await apiClient.get('/api/analytics/sessions', {
        params: {
          brandId,
          timeRange
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching session data:', error);
      throw error;
    }
  },

  // Process natural language query
  processNaturalLanguageQuery: async (query, brandId) => {
    try {
      const response = await apiClient.post('/api/analytics/query', {
        query,
        brandId
      });
      return response.data;
    } catch (error) {
      console.error('Error processing query:', error);
      throw error;
    }
  },

  // Get connector data
  getConnectorData: async (connectorId, timeRange, limit) => {
    try {
      const response = await apiClient.get(`/api/analytics/connector/${connectorId}`, {
        params: {
          timeRange,
          limit
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching connector data:', error);
      throw error;
    }
  },

  // Get available tables for brand
  getAvailableTables: async (brandId) => {
    try {
      const response = await apiClient.get(`/api/analytics/tables/${brandId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching available tables:', error);
      throw error;
    }
  },

  // Get data schema for brand
  getBrandDataSchema: async (brandId) => {
    try {
      const response = await apiClient.get(`/api/analytics/schema/${brandId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching data schema:', error);
      throw error;
    }
  },

  // Get data sources status
  getDataSourcesStatus: async (brandId) => {
    try {
      console.log(`Fetching data sources status for brand ${brandId}`);
      const response = await apiClient.get('/api/analytics/connectors/status', {
        params: {
          brandId
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching data sources status:', error);
      throw error;
    }
  },

  // Get platform breakdown (revenue, orders, customers by source_platform)
  getPlatformBreakdown: async (brandId, timeRange) => {
    try {
      console.log(`Fetching platform breakdown for brand ${brandId} with timeRange=${timeRange}`);
      const response = await apiClient.get('/api/analytics/platform-breakdown', {
        params: {
          brandId,
          timeRange: timeRange || 'week'
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching platform breakdown:', error);
      throw error;
    }
  }
};

export default dataService;
